export function Field({ label, value, onChange, placeholder = "", type = "text", testid, hint }) {
  return (
    <div>
      <label className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/45">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-body text-sm text-white placeholder-white/30 transition focus:border-[#0055ff]/50 focus:bg-white/[0.04] focus:outline-none"
      />
      {hint && <p className="mt-1.5 font-body text-[11px] text-white/40">{hint}</p>}
    </div>
  );
}

export function TextArea({ label, value, onChange, placeholder = "", rows = 4, testid, hint }) {
  return (
    <div>
      <label className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/45">
        {label}
      </label>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full resize-y rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-body text-sm leading-relaxed text-white placeholder-white/30 transition focus:border-[#0055ff]/50 focus:bg-white/[0.04] focus:outline-none"
      />
      {hint && <p className="mt-1.5 font-body text-[11px] text-white/40">{hint}</p>}
    </div>
  );
}

export function TagsField({ label, value = [], onChange, placeholder = "Press Enter to add", testid, hint }) {
  const items = Array.isArray(value) ? value : [];
  function handleKey(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const raw = e.currentTarget.value.trim().replace(/,$/, "");
      if (raw && !items.includes(raw)) onChange([...items, raw]);
      e.currentTarget.value = "";
    } else if (e.key === "Backspace" && !e.currentTarget.value && items.length) {
      onChange(items.slice(0, -1));
    }
  }
  return (
    <div>
      <label className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/45">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 focus-within:border-[#0055ff]/50">
        {items.map((tag, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-3 py-1 font-body text-xs text-white/85"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-white/40 hover:text-white"
              aria-label={`remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          onKeyDown={handleKey}
          placeholder={placeholder}
          data-testid={testid}
          className="min-w-[140px] flex-1 bg-transparent font-body text-sm text-white placeholder-white/30 focus:outline-none"
        />
      </div>
      {hint && <p className="mt-1.5 font-body text-[11px] text-white/40">{hint}</p>}
    </div>
  );
}

export function ToggleRow({ label, description, value, onChange, testid }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5 transition hover:border-white/20">
      <div>
        <div className="font-display text-sm text-white">{label}</div>
        {description && (
          <div className="mt-0.5 font-body text-xs text-white/45">{description}</div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`relative h-6 w-11 shrink-0 rounded-full border transition ${
          value
            ? "border-[#0055ff] bg-[#0055ff]"
            : "border-white/15 bg-white/[0.04]"
        }`}
      >
        <span
          className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow transition-all ${
            value ? "left-[22px]" : "left-0.5"
          }`}
        />
      </button>
    </label>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-white/[0.015] px-6 py-20 text-center">
      {Icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/55">
          <Icon className="h-5 w-5" />
        </div>
      )}
      <h3 className="font-display text-xl text-white">{title}</h3>
      {description && (
        <p className="mt-2 max-w-md font-body text-sm text-white/50">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-xl bg-white/[0.04] ${className}`}
      style={{ animationDuration: "1.6s" }}
    />
  );
}
